import { memo, useCallback, useRef, useState } from "react";
import { cn } from "@/lib/utils";

interface FileDropZoneProps {
  onFiles: (files: FileList) => void;
  disabled?: boolean;
  className?: string;
  children: React.ReactNode;
}

export const FileDropZone = memo(function FileDropZone({
  onFiles,
  disabled,
  className,
  children,
}: FileDropZoneProps) {
  const [isDragOver, setIsDragOver] = useState(false);
  const dragCounterRef = useRef(0);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current++;
    if (dragCounterRef.current === 1) setIsDragOver(true);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current--;
    if (dragCounterRef.current === 0) setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounterRef.current = 0;
      setIsDragOver(false);
      if (e.dataTransfer.files.length > 0) {
        onFiles(e.dataTransfer.files);
      }
    },
    [onFiles],
  );

  return (
    <div
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={cn(
        "relative",
        className,
        isDragOver
          ? "border-blue-500/40 bg-blue-500/[0.06]"
          : disabled
            ? "border-white/[0.06] bg-zinc-900/30 opacity-50"
            : "border-white/[0.06] bg-zinc-900/60 focus-within:border-white/[0.1]",
      )}
    >
      {children}
      {isDragOver && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-2xl">
          <span className="rounded-lg bg-blue-500/20 px-3 py-1.5 text-[13px] font-medium text-blue-300 backdrop-blur-sm">
            Drop files to attach
          </span>
        </div>
      )}
    </div>
  );
});
